import React, { useState, useRef, useEffect } from 'react';
import { 
  XMarkIcon, 
  PaperAirplaneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { aiAssistantService, ChatMessage, AiResponse } from '../../services/ai-assistant.service';

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadSuggestions();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadSuggestions = async () => {
    try {
      const data = await aiAssistantService.getSuggestions();
      setSuggestions(data);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: message,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response: AiResponse = await aiAssistantService.sendMessage(message);
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: response.response.text,
        sender: 'assistant',
        timestamp: new Date(),
        data: response.response.data,
        displayType: response.response.displayType,
        actions: response.response.actions,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };

  const renderMessageContent = (message: ChatMessage) => {
    if (message.displayType && message.data) {
      switch (message.displayType) {
        case 'products_table':
          return (
            <div className="mt-2">
              <div className="text-sm text-gray-600 mb-2">{message.text}</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {message.data.slice(0, 5).map((product: any) => (
                  <div key={product.id} className="flex justify-between text-xs bg-gray-50 p-2 rounded">
                    <span className="font-medium">{product.name}</span>
                    <span className="text-gray-500">Stock: {product.currentStock}</span>
                  </div>
                ))}
                {message.data.length > 5 && (
                  <div className="text-xs text-gray-400 text-center py-1">
                    ... and {message.data.length - 5} more
                  </div>
                )}
              </div>
            </div>
          );

        case 'low_stock_table':
          return (
            <div className="mt-2">
              <div className="text-sm text-gray-600 mb-2">{message.text}</div>
              {message.data.length > 0 ? (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {message.data.slice(0, 3).map((item: any) => (
                    <div key={item.productId} className="flex justify-between text-xs bg-red-50 p-2 rounded border border-red-200">
                      <span className="font-medium">{item.productName}</span>
                      <span className="text-red-600 font-medium">{item.currentStock} left</span>
                    </div>
                  ))}
                  {message.data.length > 3 && (
                    <div className="text-xs text-gray-400 text-center py-1">
                      ... and {message.data.length - 3} more items
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                  ✅ All items are well-stocked!
                </div>
              )}
            </div>
          );

        case 'sales_table':
          return (
            <div className="mt-2">
              <div className="text-sm text-gray-600 mb-2">{message.text}</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {message.data.slice(0, 3).map((sale: any) => (
                  <div key={sale.id} className="text-xs bg-blue-50 p-2 rounded">
                    <div className="flex justify-between">
                      <span className="font-medium">#{sale.orderNumber}</span>
                      <span className="text-blue-600">{aiAssistantService.formatCurrency(sale.grandTotal)}</span>
                    </div>
                    <div className="text-gray-500 mt-1">
                      {sale.customer?.name || 'Unknown Customer'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

        case 'customers_table':
          return (
            <div className="mt-2">
              <div className="text-sm text-gray-600 mb-2">{message.text}</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {message.data.slice(0, 3).map((customer: any) => (
                  <div key={customer.customerId} className="text-xs bg-green-50 p-2 rounded">
                    <div className="flex justify-between">
                      <span className="font-medium">{customer.customerName}</span>
                      <span className="text-green-600">{aiAssistantService.formatCurrency(customer.totalValue)}</span>
                    </div>
                    <div className="text-gray-500 mt-1">
                      {customer.totalOrders} orders
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

        case 'work_orders_table':
          return (
            <div className="mt-2">
              <div className="text-sm text-gray-600 mb-2">{message.text}</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {message.data.slice(0, 3).map((wo: any) => (
                  <div key={wo.id} className="text-xs bg-purple-50 p-2 rounded">
                    <div className="flex justify-between">
                      <span className="font-medium">#{wo.workOrderNumber}</span>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(wo.status)}`}>
                        {wo.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-gray-500 mt-1">
                      {wo.product?.name || 'Unknown Product'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

        case 'dashboard_cards':
        case 'metrics_cards':
          return (
            <div className="mt-2">
              <div className="text-sm whitespace-pre-line">{message.text}</div>
            </div>
          );

        default:
          return <div className="text-sm whitespace-pre-line">{message.text}</div>;
      }
    }

    return <div className="text-sm whitespace-pre-line">{message.text}</div>;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 rounded-full w-14 h-14 bg-blue-600 hover:bg-blue-700 shadow-lg z-50 flex items-center justify-center"
      >
        <SparklesIcon className="h-6 w-6 text-white" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 w-96 h-[500px] flex flex-col shadow-2xl z-50 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-lg">
        <div className="flex items-center space-x-2">
          <SparklesIcon className="h-5 w-5" />
          <span className="font-medium">ERP Assistant</span>
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(false)}
          className="text-white hover:bg-white/20"
        >
          <XMarkIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 text-sm">
            <div className="mb-3">👋 Hi! I'm your ERP assistant.</div>
            <div className="mb-3">Try asking me:</div>
            <div className="space-y-2">
              {suggestions.slice(0, 3).map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="block w-full text-left text-xs bg-white hover:bg-blue-50 border border-gray-200 p-2 rounded transition-colors"
                >
                  💬 {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] p-3 rounded-lg ${
                message.sender === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-900 border border-gray-200'
              }`}
            >
              {renderMessageContent(message)}
              
              {message.actions && (
                <div className="mt-2 space-y-1">
                  {message.actions.map((action, index) => (
                    <button
                      key={index}
                      className="block w-full text-left text-xs bg-blue-100 hover:bg-blue-200 p-2 rounded transition-colors"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 p-3 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t bg-white rounded-b-lg">
        <div className="flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage(input)}
            placeholder="Ask me anything about your ERP..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          />
          <Button
            onClick={() => sendMessage(input)}
            disabled={isLoading || !input.trim()}
            size="sm"
            className="px-3"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ChatBot;